import { useId, type ReactNode } from 'react'
import type { RenderEntityList as InsightEntityList } from '../../../insightRenderLists'
import { ProductPhotoDeck } from './ProductImageDeck'
import { EntityListDetailsModal, formatEntityListDatum } from './EntityListDetailsModal'
import { Note } from '../InsightsBlocks'
import { InsightEntityNames } from './InsightEntityNames'
import { useAppBridge } from '@shopify/app-bridge-react'
import { trackShopifyAction } from '../../../shopifyUiTracking'
/** Supplied entities, ordering and text; no customer calculations or ranking in React. */
export function EntityListCard({
  list,
  context,
  rowContent,
  children,
}: {
  list: InsightEntityList
  context: string
  rowContent?: Record<string, ReactNode>
  children?: ReactNode
}) {
  const shopify = useAppBridge()
  const modalId = `entity-details-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
  let message = null,
    details = null
  if (list.message) message = <Note>{list.message}</Note>
  if (list.rows.length)
    details = (
      <s-link
        commandFor={modalId}
        command="--show"
        data-entity-list-details
        onClick={() => trackShopifyAction(shopify, 'open-patterns', () => undefined)}
      >
        See {list.rows.length} {list.rows.length === 1 ? 'pattern' : 'patterns'}
      </s-link>
    )
  const rows = list.rows.slice(0, list.previewRows).map((row) => (
    <s-stack key={row.id} gap="small" data-insight-row={row.id}>
      <s-grid gridTemplateColumns="auto minmax(0, 1fr)" gap="small" alignItems="start">
        <s-stack gap="small-100">
          <ProductPhotoDeck photos={row.entities} />
          <Note>
            {row.entities
              .map((e) => e.role)
              .filter(Boolean)
              .join(' → ')}
          </Note>
        </s-stack>
        <div style={{ minInlineSize: 0, overflowWrap: 'anywhere' }}>
          <s-stack gap="small-100">
            <s-text fontWeight="semibold">
              <InsightEntityNames entities={row.entities} separator=" → " />
            </s-text>
            <s-text fontWeight="medium">{formatEntityListDatum(row.summary)}</s-text>
            <Note>{row.note}</Note>
          </s-stack>
        </div>
      </s-grid>
      {rowContent?.[row.id]}
      <s-divider />
    </s-stack>
  ))
  return (
    <s-stack gap="base" data-insight-list={list.id}>
      <s-heading>{list.title}</s-heading>
      <Note>{list.introduction}</Note>
      {message}
      {rows}
      {details}
      {children}
      <Note>{list.footer}</Note>
      <EntityListDetailsModal id={modalId} list={list} context={context} rowContent={rowContent} />
    </s-stack>
  )
}
